import React, { useState, useEffect } from 'react';
import { Shield, Plus, Search, Edit, Trash2, Eye, Users, Lock } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Button from '../../components/ui/Button';
import Input from '../../components/forms/Input';
import Modal from '../../components/ui/Modal';
import RoleForm from './RoleForm';
import RoleDetails from './RoleDetails';
import apiService from '../../services/apiService';
import './RolesPage.css';

const RolesPage = () => {
    const { loading, setLoading, setError, clearError } = useApp();

    const [roles, setRoles] = useState([]);
    const [filteredRoles, setFilteredRoles] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);

    // Cargar roles al montar el componente
    useEffect(() => {
        loadRoles();
    }, []);

    // Filtrar roles cuando cambie la búsqueda
    useEffect(() => {
        filterRoles();
    }, [roles, searchQuery]);

    const loadRoles = async () => {
        try {
            setLoading(true);
            clearError();

            const result = await apiService.getRoles();

            if (result.success) {
                setRoles(result.data);
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al cargar roles');
        } finally {
            setLoading(false);
        }
    };

    const filterRoles = () => {
        if (!searchQuery.trim()) {
            setFilteredRoles(roles);
            return;
        }

        const query = searchQuery.toLowerCase();
        const filtered = roles.filter(role =>
            role.nombre.toLowerCase().includes(query) ||
            role.descripcion?.toLowerCase().includes(query)
        );

        setFilteredRoles(filtered);
    };

    const handleCreateRole = async (roleData) => {
        try {
            const result = await apiService.crearRol(roleData);

            if (result.success) {
                setShowCreateModal(false);
                loadRoles(); // Recargar lista
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al crear rol');
        }
    };

    const handleEditRole = async (roleData) => {
        try {
            const result = await apiService.actualizarRol({
                role_id: selectedRole.id,
                ...roleData
            });

            if (result.success) {
                setShowEditModal(false);
                setSelectedRole(null);
                loadRoles(); // Recargar lista
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al actualizar rol');
        }
    };

    const handleDeleteRole = async (roleId, roleName) => {
        if (!window.confirm(`¿Estás seguro de que quieres eliminar el rol "${roleName}"?`)) {
            return;
        }

        try {
            const result = await apiService.eliminarRol({ role_id: roleId });

            if (result.success) {
                loadRoles(); // Recargar lista
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al eliminar rol');
        }
    };

    const getRoleIcon = (roleName) => {
        switch (roleName?.toLowerCase()) {
            case 'administrador':
                return <Shield size={16} className="role-icon admin" />;
            case 'gerente':
                return <Users size={16} className="role-icon manager" />;
            case 'cajero':
                return <Lock size={16} className="role-icon cashier" />;
            default:
                return <Shield size={16} className="role-icon default" />;
        }
    };

    const getRoleClass = (roleName) => {
        switch (roleName?.toLowerCase()) {
            case 'administrador':
                return 'role-badge admin';
            case 'gerente':
                return 'role-badge manager';
            case 'cajero':
                return 'role-badge cashier';
            default:
                return 'role-badge default';
        }
    };

    return (
        <div className="roles-page">
            {/* Header */}
            <div className="roles-header">
                <div className="header-content">
                    <h1 className="page-title">
                        <Shield size={24} />
                        Gestión de Roles y Permisos
                    </h1>

                </div>

                <div className="header-actions">
                    <Button
                        onClick={() => setShowCreateModal(true)}
                        className="create-role-btn"
                        disabled={loading}
                    >

                        Nuevo Rol
                    </Button>
                </div>
            </div>

            {/* Filtros y búsqueda */}
            <div className="roles-filters">
                <div className="search-container">
                    <Input
                        placeholder="Buscar por nombre o descripción..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        icon={<Search size={20} />}
                        className="search-input"
                    />
                </div>

                <div className="filters-summary">
                    <span className="roles-count">
                        {filteredRoles.length} de {roles.length} roles
                    </span>
                </div>
            </div>

            {/* Tabla de roles */}
            <div className="roles-table-container">
                {loading && (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Cargando roles...</p>
                    </div>
                )}

                {!loading && filteredRoles.length === 0 && (
                    <div className="empty-state">
                        <Shield size={48} />
                        <h3>No se encontraron roles</h3>
                        <p>
                            {searchQuery
                                ? 'No hay roles que coincidan con tu búsqueda'
                                : 'No hay roles registrados en el sistema'
                            }
                        </p>
                        {!searchQuery && (
                            <Button
                                onClick={() => setShowCreateModal(true)}
                                className="empty-action-btn"
                            >

                                Crear primer rol
                            </Button>
                        )}
                    </div>
                )}

                {!loading && filteredRoles.length > 0 && (
                    <table className="roles-table-compact">
                        <thead>
                            <tr>
                                <th width="20%">Rol</th>
                                <th width="30%">Descripción</th>
                                <th width="10%">Usuarios</th>
                                <th width="15%">Necesita Almacén</th>
                                <th width="10%">Estado</th>
                                <th width="15%">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRoles.map((role) => (
                                <tr key={role.id} className={role.activo ? '' : 'inactive-row'}>
                                    <td>
                                        <div className="role-compact">
                                            <div className="role-icon-container">
                                                {getRoleIcon(role.nombre)}
                                            </div>
                                            <div className="role-info-compact">
                                                <div className="role-name-compact">{role.nombre}</div>
                                            </div>
                                        </div>
                                    </td>

                                    <td>
                                        <div className="description-compact">
                                            <p className="description-text">
                                                {role.descripcion || 'Sin descripción'}
                                            </p>
                                            <div className="permissions-count">
                                                {role.permisos_count || 0} permisos
                                            </div>
                                        </div>
                                    </td>

                                    <td>
                                        <div className="users-count-compact">
                                            <Users size={14} />
                                            <span>{role.usuarios_count || 0}</span>
                                        </div>
                                    </td>

                                    <td>
                                        <span className={`almacen-badge ${role.necesita_almacen ? 'si' : 'no'}`}>
                                            {role.necesita_almacen ? 'Sí' : 'No'}
                                        </span>
                                    </td>

                                    <td>
                                        <span className={`status-dot ${role.activo ? 'active' : 'inactive'}`}>
                                            {role.activo ? '● Activo' : '● Inactivo'}
                                        </span>
                                    </td>

                                    <td>
                                        <div className="actions-compact">
                                            <button
                                                className="action-btn-compact"
                                                onClick={() => {
                                                    setSelectedRole(role);
                                                    setShowDetailsModal(true);
                                                }}
                                                title="Ver detalles"
                                            >
                                                <Eye size={14} />
                                            </button>

                                            <button
                                                className="action-btn-compact"
                                                onClick={() => {
                                                    setSelectedRole(role);
                                                    setShowEditModal(true);
                                                }}
                                                title="Editar"
                                            >
                                                <Edit size={14} />
                                            </button>

                                            <button
                                                className="action-btn-compact delete"
                                                onClick={() => handleDeleteRole(role.id, role.nombre)}
                                                title="Eliminar"
                                                disabled={role.usuarios_count > 0}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modales */}
            {showCreateModal && (
                <Modal
                    title="Crear Nuevo Rol"
                    onClose={() => setShowCreateModal(false)}
                    size="large"
                >
                    <RoleForm
                        onSubmit={handleCreateRole}
                        onCancel={() => setShowCreateModal(false)}
                        isLoading={loading}
                    />
                </Modal>
            )}

            {showEditModal && selectedRole && (
                <Modal
                    title="Editar Rol"
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedRole(null);
                    }}
                    size="large"
                >
                    <RoleForm
                        role={selectedRole}
                        onSubmit={handleEditRole}
                        onCancel={() => {
                            setShowEditModal(false);
                            setSelectedRole(null);
                        }}
                        isLoading={loading}
                        isEdit={true}
                    />
                </Modal>
            )}

            {showDetailsModal && selectedRole && (
                <Modal
                    title="Detalles del Rol"
                    onClose={() => {
                        setShowDetailsModal(false);
                        setSelectedRole(null);
                    }}
                    size="large"
                >
                    <RoleDetails
                        role={selectedRole}
                        onClose={() => {
                            setShowDetailsModal(false);
                            setSelectedRole(null);
                        }}
                        onEdit={() => {
                            setShowDetailsModal(false);
                            setShowEditModal(true);
                        }}
                    />
                </Modal>
            )}
        </div>
    );
};

export default RolesPage;